import { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import { apiErrorMessage } from "../services/api";
import { fetchDiary } from "../services/bookingService";
import { addDays, todayIn, weekOf } from "../services/datetime";

/**
 * One branch's diary over a date range.
 *
 * The view decides the range: a day view asks for one date, a week view for
 * seven, an agenda for whatever span it shows. Everything else — the layers,
 * the branch timezone, moving forward and back — is the same regardless, so it
 * lives here rather than in three views.
 *
 * `timezone` comes from the API rather than the browser. A branch in Riyadh
 * viewed from London draws on Riyadh's clock, and "today" means today there.
 */
/**
 * `urlDate` / `setUrlDate` come from the page's URL state. With no date in the
 * URL the diary shows the branch's own today, which only the API knows — it is
 * kept here as the default rather than written into the URL.
 */
export default function useDiary(
  branchId,
  view = "day",
  urlDate = "",
  setUrlDate = () => {},
  providerIds = [],
  onInvalidProviders = () => {}
) {
  const { t } = useTranslation();

  const [serverToday, setServerToday] = useState(null);
  const date = urlDate || serverToday;
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  // The range this view needs, derived from the anchor date.
  const range = useMemo(() => {
    if (!date) return null;

    if (view === "week") {
      const days = weekOf(date);
      return { from: days[0], to: days[6], days };
    }

    if (view === "agenda") {
      // A fortnight ahead: far enough to plan around, short enough to read.
      return { from: date, to: addDays(date, 13), days: null };
    }

    return { from: date, to: date, days: [date] };
  }, [date, view]);

  // A stable key, so a new array with the same ids does not refetch.
  const providerKey = providerIds.join(",");

  const load = useCallback(async () => {
    if (!branchId) return;
    const providers = providerKey ? providerKey.split(",") : [];

    setLoading(true);
    setError("");

    try {
      // Without an anchor date yet, ask for the branch's own today — the API
      // answers in its timezone and hands the date back in `meta`.
      const result = range
        ? await fetchDiary(branchId, range.from, range.to, providers)
        : await fetchDiary(branchId, null, null, providers);

      setData(result);

      // First load settles the anchor on whatever the branch calls today.
      if (!date && result.meta?.from) setServerToday(result.meta.from);
    } catch (err) {
      // A link with providers from another branch: drop the filter, reload.
      if (err.response?.status === 422 && err.response?.data?.errors?.provider_ids && providers.length) {
        onInvalidProviders();
        return;
      }
      setError(apiErrorMessage(err, t("diary.loadFailed")));
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [branchId, range, date, t, providerKey, onInvalidProviders]);

  useEffect(() => {
    load();
  }, [load]);

  const timezone = data?.meta?.timezone ?? "UTC";

  /** Move by one unit of whatever is being shown. */
  const step = useCallback(
    (direction) => {
      if (!date) return;
      const by = view === "week" ? 7 : view === "agenda" ? 14 : 1;
      setUrlDate(addDays(date, direction * by));
    },
    [date, view, setUrlDate]
  );

  // Today is the default: clear the date from the URL rather than write it.
  const goToday = useCallback(() => {
    setServerToday(todayIn(timezone));
    setUrlDate("");
  }, [timezone, setUrlDate]);

  return {
    date,
    setDate: setUrlDate,
    range,
    timezone,
    appointments: data?.appointments ?? [],
    blocks: data?.blocks ?? [],
    // The columns drawn — already narrowed by the provider filter.
    providers: data?.providers ?? [],
    // Who the provider filter may offer (and the booking / block forms list):
    // every provider of the branch in reach, whatever is filtered.
    providerOptions: data?.meta?.provider_options ?? [],
    workingHours: data?.working_hours ?? [],
    // Per status across the whole range, whatever is filtered on screen.
    statusCounts: data?.meta?.status_counts ?? null,
    loading,
    error,
    reload: load,
    step,
    goToday,
  };
}
